import { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { getMe, logout as apiLogout } from "@/lib/api";

const AuthContext = createContext(null);

// A single retry for network-level failures (cold start, deploy window,
// TLS blip). Does not apply to 4xx responses — those are definitive.
const AUTH_RETRY_DELAY_MS = 1200;

function isNetworkError(err) {
  // axios: `err.response` is undefined when the request never got an
  // HTTP reply (ERR_FAILED, ERR_NETWORK, transport timeout, TLS error).
  return !err?.response;
}

function isAuthError(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCheckDone = useRef(false);
  const refreshInFlightRef = useRef(null);

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem("token");
    } catch {}
    setUser(null);
  }, []);

  // /auth/me with a single retry on network errors only.
  // 401/403 → don't retry, propagate. 500 → don't retry, propagate.
  // Network (no response) → retry once after a short delay.
  const getMeWithRetry = useCallback(async () => {
    try {
      return await getMe();
    } catch (err) {
      if (isNetworkError(err)) {
        console.warn(
          "[Auth] /auth/me network error — retrying once in",
          AUTH_RETRY_DELAY_MS,
          "ms",
        );
        await new Promise((r) => setTimeout(r, AUTH_RETRY_DELAY_MS));
        return await getMe();
      }
      throw err;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const promise = (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setUser(null);
          return null;
        }
        const u = await getMeWithRetry();
        if (!u || !u.user_id) {
          clearSession();
          return null;
        }
        setUser(u);
        return u;
      } catch (err) {
        if (isAuthError(err)) {
          console.warn("[Auth] refresh: auth error", err.response?.status);
          clearSession();
          return null;
        }
        if (isNetworkError(err)) {
          // Still unreachable after retry — keep the session so a
          // reload can succeed once the backend recovers.
          console.warn(
            "[Auth] refresh: network error after retry — keeping session",
          );
          toast.error(
            "Can't reach the server right now. Some features may not work.",
            { id: "auth-network-error" },
          );
          return user; // no state change
        }
        console.warn("[Auth] refresh: unexpected error", err);
        clearSession();
        return null;
      }
    })();
    refreshInFlightRef.current = promise;
    try {
      return await promise;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [clearSession, getMeWithRetry, user]);

  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;

    let isMounted = true;
    (async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        if (isMounted) setLoading(false);
        return;
      }
      try {
        const u = await getMeWithRetry();
        if (isMounted) {
          if (u && u.user_id) {
            setUser(u);
          } else {
            try { localStorage.removeItem("token"); } catch {}
            setUser(null);
          }
        }
      } catch (err) {
        if (isNetworkError(err)) {
          // Transient — keep the token so a reload can succeed.
          console.warn(
            "[Auth] initial check: network error — keeping token for retry",
          );
          if (isMounted) setUser(null);
        } else {
          try { localStorage.removeItem("token"); } catch {}
          if (isMounted) setUser(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [getMeWithRetry]);

  useEffect(() => {
    const onUnauthorized = () => clearSession();
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, [clearSession]);

  useEffect(() => {
    const onLegalRequired = () => {
      refresh().catch(() => {});
    };
    window.addEventListener("haven:legal-required", onLegalRequired);
    return () => window.removeEventListener("haven:legal-required", onLegalRequired);
  }, [refresh]);

  // Global 402 handler — belt-and-braces. Individual pages (MatchChat,
  // etc.) show their own dialogs; this catches any 402 a caller swallowed.
  useEffect(() => {
    const onTokensRequired = (e) => {
      const msg = e.detail?.message || "You're out of tokens.";
      toast.error(msg, {
        action: {
          label: "Earn tokens",
          onClick: () => {
            window.location.href = "/earn-tokens";
          },
        },
        duration: 6000,
      });
    };
    window.addEventListener("haven:tokens-required", onTokensRequired);
    return () =>
      window.removeEventListener("haven:tokens-required", onTokensRequired);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.warn("Logout API failed:", err);
    }
    clearSession();
  }, [clearSession]);

  const legalRequired = user?.legal_acceptance?.required === true;

  const value = {
    user,
    setUser,
    loading,
    refresh,
    logout,
    isAuthenticated: !!user,
    updateRequired: false,
    legalRequired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export default AuthProvider;